import type { MessageDescriptor } from '@lingui/core';
import { useMemo, useState, useCallback } from "react";
import { BaseEditableTable } from "./BaseEditableTable";
import type { EditableCellConfig } from "./types";
import type { ActivityResponse, ActivityListParams } from "@/types/activities";
import { getActivityTypeLabels } from "@/types/activities";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { getActivityCSVColumns } from "@/utils/csvExport";
import {
  fetchAllActivitiesForExport,
  EXPORT_MAX_ROWS,
} from "@/hooks/useActivitiesExport";
import type { RowSelectionState, PaginationState } from "@tanstack/react-table";
import { useNavigate } from "react-router";
import { useLingui } from "@lingui/react/macro";
import { t } from "@lingui/macro";
import { getActivityColumns } from "@/utils/columnDefinitions";

interface ActivitiesTableProps {
  data: ActivityResponse[];
  enableSelection?: boolean;
  onRowClick?: (activityId: string) => void;
  // Server-side pagination props (see BaseEditableTable types.ts)
  serverSidePagination?: boolean;
  totalRows?: number;
  pagination?: PaginationState;
  onPaginationChange?: (
    updater: PaginationState | ((old: PaginationState) => PaginationState)
  ) => void;
  isFetching?: boolean;
  /**
   * Active server-side filter params. In server-side mode "Export all
   * matching" pages the server with these filters instead of exporting only
   * the loaded rows.
   */
  exportParams?: ActivityListParams;
  /** The first page is loading: skeleton rows instead of the empty state. */
  isLoading?: boolean;
}

export function ActivitiesTable({
  data,
  enableSelection = true,
  onRowClick,
  serverSidePagination = false,
  totalRows,
  pagination,
  onPaginationChange,
  isFetching,
  exportParams,
  isLoading,
}: ActivitiesTableProps) {
  const [selectedRows, setSelectedRows] = useState<RowSelectionState>({});
  const navigate = useNavigate();
  const { i18n } = useLingui();

  // Build activity type options for filter dropdown
  const activityTypeOptions = useMemo(() => {
    const labels = getActivityTypeLabels(i18n);
    return Object.entries(labels).map(([value, label]) => ({
      value,
      label,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n.locale]);

  // Define the columns for the activities table
  const columns = useMemo<EditableCellConfig<ActivityResponse>[]>(
    () =>
      getActivityColumns(
        // Widened to `TranslationFunction`'s `string | MessageDescriptor` parameter. A
        // descriptor is passed straight to `i18n._`; the string branch keeps the existing
        // message-id lookup, so runtime behaviour for the real call sites is unchanged.
        (messageId: string | MessageDescriptor): string => {
          if (typeof messageId !== "string") {
            return i18n._(messageId);
          }
          const translations: Record<string, string> = {
            "table.activities.columns.childName": i18n._(
              t({ id: "table.activities.columns.childName", message: "Child Name" })
            ),
            "table.activities.columns.type": i18n._(
              t({ id: "table.activities.columns.type", message: "Type" })
            ),
            "table.activities.columns.activityTime": i18n._(
              t({ id: "table.activities.columns.activityTime", message: "Activity Time" })
            ),
            "table.activities.columns.recordedBy": i18n._(
              t({ id: "table.activities.columns.recordedBy", message: "Recorded By" })
            ),
            "table.activities.columns.createdAt": i18n._(
              t({ id: "table.activities.columns.createdAt", message: "Created At" })
            ),
          };
          return translations[messageId] || messageId;
        },
        navigate,
        { activityTypeOptions, onActivityClick: onRowClick }
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [i18n.locale, navigate, activityTypeOptions, onRowClick]
  );

  // Create localized CSV columns - pass locale to trigger recomputation on language change
  const localizedActivityCSVColumns = useMemo(() => {
    return getActivityCSVColumns(i18n.locale);
  }, [i18n.locale]);

  // Only "Clear selection" lives here; exporting the selection is in the
  // table's Export menu.
  const renderSelectionActions = useCallback(
    (_selectedRowIds: string[], clearSelection: () => void) => (
      <Button variant="ghost" size="sm" onClick={clearSelection}>
        {i18n._(
          t({ id: "table.activities.actions.clearSelection", message: "Clear Selection" })
        )}
      </Button>
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [i18n.locale]
  );

  // "Export all matching" in server-side mode: page the server with the active
  // filters, not just the rows of the current page. The base table writes the
  // CSV and reports the row count; the cap is said out loud here.
  const exportAll = useCallback(async (): Promise<ActivityResponse[]> => {
    const { rows, truncated } = await fetchAllActivitiesForExport(
      exportParams ?? {}
    );
    if (truncated) {
      toast.warning(
        i18n._({
          id: "table.activities.actions.exportTruncated",
          message:
            "Export truncated to {cap} rows. Narrow the filter to export everything.",
          values: { cap: EXPORT_MAX_ROWS },
        })
      );
    }
    return rows;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exportParams, i18n.locale]);

  // No-op cell update handler since activities are read-only
  const noOpCellUpdate = async (): Promise<void> => {
    // Activities are read-only from this view
  };

  return (
    <BaseEditableTable<ActivityResponse>
      data={data}
      columns={columns}
      onCellUpdate={noOpCellUpdate}
      defaultSortField="activity_time"
      defaultSortOrder="desc"
      defaultPageSize={20}
      enableSelection={enableSelection}
      selectedRows={selectedRows}
      onSelectionChange={setSelectedRows}
      renderSelectionActions={renderSelectionActions}
      // Server-side filtering is done via the AttendanceFilterBar; the backend
      // list endpoint has no free-text search, so we disable the client search
      // box in server mode to avoid a control that would silently filter only
      // the current page.
      enableSearch={!serverSidePagination}
      searchPlaceholder={i18n._(
        t({ id: "table.activities.search.placeholder", message: "Search activities..." })
      )}
      enableFilters={false}
      enableColumnVisibility={true}
      enableColumnOrdering={true}
      tableId="activities-table"
      serverSidePagination={serverSidePagination}
      totalRows={totalRows}
      pagination={pagination}
      onPaginationChange={onPaginationChange}
      isFetching={isFetching}
      isLoading={isLoading}
      enableExport
      exportAll={serverSidePagination ? exportAll : undefined}
      exportColumns={localizedActivityCSVColumns}
      exportFileName="activities"
    />
  );
}
